/* istanbul ignore file */
import React from 'react';
import { Provider } from 'react-redux';
import type { FeatureFlags } from '@noths/polaris-client-feature-flags';
import { CookieModal } from '@noths/polaris-client-gdpr-compliance';
import { GTMWithDataLayer } from '@noths/polaris-client-google-analytics';
import { setLocaleCookies } from '@noths/polaris-client-localisation';
import { ErrorBoundary, getLayout, LayoutTypes } from '@noths/polaris-client-next-components';
import {
  FontFaces as LegacyFontFaces,
  Palette as LegacyPalette,
} from '@noths/polaris-client-ribbons-base';
import type { SiteChromeContent } from '@noths/polaris-client-site-chrome-ui';
import { SkipLinks, useSmartAppBanner } from '@noths/polaris-client-site-chrome-ui';
import { FavouritesContainer } from '@noths/polaris-client-user-favourites';
import { GlobalStyles } from '@noths/polaris-design-system-theme-brand2026customer';
import type { Merge } from '@noths/polaris-dev-ts-recipes';
import type { AppContextPlusCookies } from '@noths/polaris-dev-ts-types';
import type { Store } from '@reduxjs/toolkit';
import { ConnectedRouter } from 'connected-next-router';
import type { AppInitialProps, AppProps } from 'next/app';
import NextApp from 'next/app';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import Head from 'next/head';

import { PRODUCT_CONTAINER_WRAPPER, PRODUCTS_GRID_WRAPPER } from 'src/constants/elementIds';
import { isProd } from 'src/environment';
import { getFeatureFlagsWithMatchers } from 'src/featureFlags/getFeatureFlagsWithMatchers';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { useStore } from 'src/redux/store';
import { fetchGlobalContent } from 'src/services/contentstack/fetchGlobalContent';
import { filterNavFilterScript } from 'src/tracking/events/pageEvent';
import type { NextConfig } from 'src/types/nextConfig';
import type { PageProps } from 'src/types/page';
import { getPreloadProductImages } from 'src/utils/getPreloadProductImages';

const BrowserRum = dynamic(() =>
  import(
    /* webpackChunkName: "datadog_browser_rum" */ '@noths/polaris-client-next-components/src/monitoring/BrowserRum'
  ).then((mod) => mod.BrowserRum),
);

const BrowserLogs = dynamic(() =>
  import(
    /* webpackChunkName: "datadog_browser_logs" */ '@noths/polaris-client-next-components/src/monitoring/BrowserLogs'
  ).then((mod) => mod.BrowserLogs),
);

const nextConfig = getConfig() as NextConfig;
const { publicRuntimeConfig } = nextConfig;
const { version } = publicRuntimeConfig;

type CustomAppProps = Merge<
  AppProps & {
    experiments?: string;
    featureFlags?: FeatureFlags;
    layoutType?: LayoutTypes;
    requestIsFromBot?: boolean;
    searchedTerm?: string;
    siteChromeCompactMode?: boolean;
    siteChromeContent?: SiteChromeContent | null;
    stickyTopBanner?: boolean;
  },
  {
    pageProps: PageProps;
  }
>;

const App = (props: CustomAppProps) => {
  const { pageProps } = props;
  const { pageEventData, siteChromeContent, updatedReduxState } = pageProps || {};
  const store = useStore(props, updatedReduxState as ReduxApplicationState);
  const products = updatedReduxState?.products;
  const productsQuery = updatedReduxState?.productsQuery;
  const searchedTerm = productsQuery?.searchTerm || '';

  // Preload the first row of product images to improve LCP.
  // Note: for LCP it may be sufficient to preload the first product image only - further testing needed.
  const preloadImages = getPreloadProductImages(products);

  useSmartAppBanner();

  const { ENABLE_LOGGING, NODE_ENV, monitoring } = publicRuntimeConfig;

  const errorBoundaryProps = {
    env: NODE_ENV,
    isLoggingEnabled: ENABLE_LOGGING,
  };

  const defaultLayoutType = LayoutTypes.CLIENT_SITE_CHROME_UI;
  const layoutType = props.layoutType ?? defaultLayoutType;
  const LayoutComponent = getLayout(layoutType);
  const stickyTopBanner =
    props.stickyTopBanner ?? (siteChromeContent?.topBannerMessages?.length ?? 0) > 0;
  const enableMonitoring = monitoring?.applicationId && isProd();

  return (
    <>
      <Head>
        {preloadImages.map((imageSrc: string) => (
          <link as="image" href={imageSrc} key={imageSrc} rel="preload" />
        ))}
        <CookieModal useTestScript={!isProd()} />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
      </Head>
      <GTMWithDataLayer customScript={filterNavFilterScript} pageEventData={pageEventData} />
      <SkipLinks
        links={[
          { content: 'Skip to content', sectionId: PRODUCT_CONTAINER_WRAPPER },
          { content: 'Skip to products', sectionId: PRODUCTS_GRID_WRAPPER },
        ]}
      />
      <Provider store={store as Store}>
        <ConnectedRouter>
          <FavouritesContainer nextConfig={nextConfig} />
          {enableMonitoring && !props.requestIsFromBot && (
            <BrowserRum
              applicationId={monitoring?.applicationId}
              clientToken={monitoring?.clientToken}
              featureFlags={props.featureFlags}
              monitoringEnv={monitoring?.monitoringEnv}
              rumSampleRate={monitoring?.rumSampleRate}
              rumTracingURLs={monitoring?.rumTracingURLs}
              serviceName={monitoring?.serviceName}
              version={version}
            />
          )}
          {enableMonitoring && (
            <BrowserLogs
              clientToken={monitoring?.clientToken}
              forwardConsoleLogs={monitoring.forwardConsoleLogs}
              logsLoggerLevel={monitoring?.logsLoggerLevel}
              logsSampleRate={monitoring?.logsSampleRate}
              monitoringEnv={monitoring?.monitoringEnv}
              serviceName={monitoring?.serviceName}
              version={version}
            />
          )}
          <LegacyFontFaces />
          <LegacyPalette />
          <GlobalStyles />
          <LayoutComponent
            NODE_ENV={NODE_ENV}
            enableCurator
            featureFlags={props.featureFlags}
            isProdLikeEnv={isProd()}
            mergeBasket={undefined}
            searchedTerm={searchedTerm}
            siteChromeCompactMode={props.siteChromeCompactMode ?? false}
            siteChromeContent={siteChromeContent ?? undefined}
            stickyTopBanner={stickyTopBanner}
          >
            <ErrorBoundary {...errorBoundaryProps}>
              <props.Component {...props.pageProps} />
            </ErrorBoundary>
          </LayoutComponent>
        </ConnectedRouter>
      </Provider>
    </>
  );
};

/*
  Necessary to disable next's Automatic Static Optimization feature.
  Without it this page will be statically compiled, and throw an error in
  `src/utils/gaPageEvent` due to undefined `pageProps.updatedReduxState`.

  Added in https://github.com/notonthehighstreet/polaris/pull/597.
  See below discussion for more details:
  https://github.com/notonthehighstreet/polaris/pull/597#discussion_r558444865
*/

App.getInitialProps = async (
  appContext: AppContextPlusCookies,
): Promise<AppInitialProps & Pick<CustomAppProps, 'featureFlags' | 'siteChromeContent'>> => {
  const {
    ctx: { req, res },
    router,
  } = appContext;

  if (req && res) {
    setLocaleCookies({ req, res });
  }

  const featureFlags = (req && getFeatureFlagsWithMatchers(req)) || {};
  const appProps = await NextApp.getInitialProps(appContext);

  if (router.route === '/404') {
    const [siteChromeContent] = await fetchGlobalContent({ featureFlags });

    return {
      ...appProps,
      siteChromeContent,
      featureFlags,
    };
  }

  return {
    ...appProps,
    featureFlags,
  };
};

export default App;
